import { useState, useRef, useEffect, useReducer } from "react";
import { useSelector } from "react-redux";

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import DXF from "../map/DxfViewer";
import mainFont from "../../../../fonts/RobotoCondensed-Regular.ttf";
import { getRotatedElementWrapperSize } from "../../../../utils/Utils";
import store from "../../../../store";

const defaultFillColor = 'black';
const defaultStrokeColor = 'rgba(0,0,0,0.3)';
const frameFillColor = 'yellow';
const frameStrokeColor = 'gray';

const fonts = [mainFont];

function FloorPlanPublicView2D({storeData, selectedElement, setSelectedElement})
{
    const panZoomRef = useRef(null);
	const canvasRef = useRef(null);

    const floorMapLayerProperties = useSelector((state) => state.store.floorMapLayerProperties);

    const [scaleStep, setScaleStep] = useState(0.01);
    const [panZoomState, setPanZoomState] = useState({ ox: 0, oy: 0, s: 1 });
    const [panZoomDisabled, setPanZoomDisabled] = useState(false); // TODO: move to global state
    const [dxfLayers, setDxfLayers] = useState([]);
    const [dxfLayersVisibility, setDxfLayersVisibility] = useState([]);
    const [dxfInfo, setDxfInfo] = useState({ size: { width: 1, height: 1 }, scale: 1 });
    const [dxfView, setDxfView] = useState({ x: 0, y: 0, z: 1, w: 0, h : 0 });
    const [dxfRelativeScale, setDxfRelativeScale] = useState(1);
    const [dxfOffset, setDxfOffset] = useState({ x: 0, y: 0 });

    const st = storeData && storeData.floor_plan ? storeData.floor_plan : null; // store

    const stw = st ? st.width : 100; // store width
    const std = st ? st.depth : 100; // store depth
    const sts = st ? st.scale : 100; // store scale
    const tx = storeData && storeData.texture ? storeData.texture : null; // texture
    const txw = tx ? tx.width : 1; // texture width
    const txd = tx ? tx.depth : 1; // texture depth
    const txpx = tx ? tx.positionX : 0; // texture pos x
    const txpy = tx ? tx.positionY : 0; // texture pos y

    const txo = storeData && storeData.dxf ? storeData.dxf : null; // texture orig
    const isDXF = txo && txo.endsWith('.dxf');

    const elements = storeData ? storeData.floor_plan_elements : [];

    useEffect(() => {
        console.log(storeData);
        reCenterMap();
    }, []);

    useEffect(() => {
        const { size, scale } = dxfInfo;
        const relativeScale = (size.width * scale) / stw;

        setDxfRelativeScale(relativeScale);
    }, [dxfInfo, stw]);

    const updateDxfView = (state) => {
        if(!panZoomRef.current) {
            return;
        }

        const { ox, oy, s } = state;
        const pz = panZoomRef.current.instance; // panzoom
        const c = pz.wrapperComponent; // panzoom container
        const dc = pz.contentComponent; // panzoom drag container
        const w = c.offsetWidth || 1; // container width
        const h = c.offsetHeight || 1; // container height
        const dcw = dc.offsetWidth || 1; // drag container width
        const dch = dc.offsetHeight || 1; // drag container height
        const z = dcw * s / w; // zoom (relative scale)
        const x = ((ox + dcw*s/2) / w - 0.5) / z; // normalized x offset
        const y = ((oy + dch*s/2) / h - 0.5) / z; // normalized y offset
        const fixed = { w, h, z, x, y };

        setDxfView(fixed);
    };

    useEffect(() => {
        updateDxfView(panZoomState);
    }, [stw, std, sts]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setDxfOffset({ x: Number(txpx) * sts, y: Number(txpy) * sts });
    }, [txpx, txpy]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        window.addEventListener('resize', reCenterMap);
        return () => window.removeEventListener('resize', reCenterMap);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!floorMapLayerProperties || !storeData) {
            return;
        }

        const width = storeData.floor_plan.width;
        const depth = storeData.floor_plan.depth;
        const positionX = storeData.texture.positionX;
        const positionY = storeData.texture.positionY;

        const clone = storeData;

        if(width !== floorMapLayerProperties.width) {
            clone.floor_plan.width = floorMapLayerProperties.width;
        }

        if (depth !== floorMapLayerProperties.depth) {
            clone.floor_plan.depth = floorMapLayerProperties.depth;
        }

        if (positionX !== floorMapLayerProperties.offsetX) {
            clone.texture.positionX = floorMapLayerProperties.offsetX;
        }

        if (positionY !== floorMapLayerProperties.offsetY) {
            clone.texture.positionY = floorMapLayerProperties.offsetY;
        }
        
        updateDxfView({ ox: floorMapLayerProperties.offsetX, oy: floorMapLayerProperties.offsetY });
    }, [floorMapLayerProperties]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * PanZoom on transformed callback
     * 
     * @param {*} state 
     */
    const onTransformed = (state) => {
        setPanZoomState(state);
        updateDxfView(state); // TODO: check to remove
    };

    /**
     * DXF file loaded callback
     * 
     * @param {array} layers
     * @param {array} layers
     * @param {array} layers
     */
    const onDxfLoaded = ({ layers, size, scale }) => {
        setDxfLayers(layers);
        setDxfInfo({ size, scale });
        reCenterMap();
    };

    const reCenterMap = () => {
        const zoomToElement = panZoomRef?.current?.zoomToElement;

        zoomToElement('FloorLayer');
    }

    const handleElementClick = (element) => {
        if(selectedElement && selectedElement.id === element.id) {
            setSelectedElement(null);
        } else {
            setSelectedElement(element);
        }
    }

    return (
        <div id="container-wrapper" style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <div
                id="container"
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#afb8c0',
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                {isDXF && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%'
                        }}
                    >
                        <DXF
                            url={txo}
                            fonts={fonts}
                            x={dxfView.x}
                            y={dxfView.y}
                            zoom={dxfView.z}
                            width={dxfView.w}
                            height={dxfView.h}
                            scale={dxfRelativeScale}
                            ox={dxfOffset.x}
                            oy={dxfOffset.y}
                            onLoaded={onDxfLoaded}
                            layers={dxfLayersVisibility}
                        />
                    </div>
                )}

                <TransformWrapper
                    initialScale={0.01}
                    minScale={0.01}
                    //maxScale={0.01}
                    wheel={{ step: scaleStep }}
                    ref={panZoomRef}
                    centerOnInit={true}
                    velocityAnimation={{disabled: true}}
                    alignmentAnimation={{disabled: true}}
                    zoomAnimation={{disabled: true}}
                    centerContent={false}
                    disabled={panZoomDisabled}
                    onTransformed={(panZoomRef, props) => {
                        onTransformed({ ox: props.positionX, oy: props.positionY, s: props.scale });
                    }}
                    attr={{
                        'style': {backgroundColor: '#000'}
                    }}
                >
                    {(utils) => (
                        <TransformComponent>
                            <canvas
                                ref={canvasRef}
                                style={{
                                    position: 'absolute',
                                    top: (txpy * sts) + 'px',
                                    left: (txpx * sts) + 'px',
                                    width: txw  + 'px',
                                    height: txd + 'px',
                                }}
                            />
                            {!isDXF && <div
                                id='FloorLayer'
                                style={{
                                    position: 'absolute',
                                    width: stw * 100 + 'px',
                                    height: std * 100 + 'px',
                                    background: 'rgba(127,127,127,0.25)',
                                }}
                            />}
                            <svg
                                id='ElementsLayer'
                                width={stw * 100}
                                height={std * 100}
                                style={{position: 'relative'}}
                            >
                                <rect
                                    id='FloorElement'
                                    x={0}
                                    y={0}
                                    width={stw * 100}
                                    height={std * 100}
                                    fill={'dimgray'}
                                    fillOpacity={0.05}
                                    stroke={'dimgray'}
                                    strokeOpacity={0.2}
                                    strokeWidth={1/sts}
                                />
                                <g id="ElementsGroup">
                                    {elements.map((element) => {
                                        const id = element.id;
                                        const nodeId = `Element_${id}`
                                        const frameNodeId = `ElementFrame_${id}`;
                                        const px = Number(element.positionX);
                                        const py = Number(element.positionY);
                                    
                                        const { width: ww, depth: wd } = getRotatedElementWrapperSize(element);
                                    
                                        const diffWidth = (ww - element.width) / 2;
                                        const diffDepth = (wd - element.depth) / 2;

                                        const angle = Number(element.angle) || 0;
                                        const rotate = `rotate(${angle}, ${px + ww/2}, ${py + wd/2})`;
                                        const isRotated = angle !== 0;
                                
                                        return (
                                            <g key={id}>
                                                <rect
                                                    id={nodeId}

                                                    x={px + diffWidth}
                                                    y={py + diffDepth}
                                                    width={element.width}
                                                    height={element.depth}

                                                    fill={element.hex || defaultFillColor}
                                                    //stroke={defaultStrokeColor}
                                                    //strokeWidth={1}

                                                    stroke={selectedElement && selectedElement.id == nodeId ? '#FFFFFF' : defaultFillColor}
                                                    strokeWidth={selectedElement && selectedElement.id == nodeId ? 10 : 1}
                                                    
                                                    transform={rotate}
                                                    onClick={() => handleElementClick(element)}
                                                />
                                                <rect
                                                    id={frameNodeId}

                                                    x={px}
                                                    y={py}
                                                    width={ww}
                                                    height={wd}

                                                    fill={frameFillColor}
                                                    fillOpacity={0.01}
                                                    stroke={frameStrokeColor}
                                                    strokeWidth={1/sts}
                                                    strokeDasharray={2/sts}
                                                    strokeOpacity={0.5}

                                                    visibility={isRotated ? 'visible' : 'hidden'}
                                                    onClick={() => handleElementClick(element)}
                                                />
                                            </g>
                                        );
                                    })}
                                </g>
                            </svg>
                        </TransformComponent>
                    )}
                </TransformWrapper>
            </div>
        </div>
    );
}

export default FloorPlanPublicView2D;